import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, DollarSign, ShoppingCart, Receipt, Users, RotateCcw } from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import StatsGrid, { Stat } from '@/client/components/common/StatsGrid';
import { usePromotionMetricsQuery } from '@/client/api/promotion-management/promotion-metrics.query';
import { getApiErrorMessage } from '@/client/utils/errors';

import {
  MIN_YEAR,
  isoDay,
  makeColorScale,
  money,
  resolveRange,
  toTrendSeries,
  type RangePreset,
} from './PromotionMetrics/analytics';
import { MetricsToolbar } from './PromotionMetrics/MetricsToolbar';
import { RevenueTrendChart } from './PromotionMetrics/RevenueTrendChart';
import { CategoryBreakdown } from './PromotionMetrics/CategoryBreakdown';
import { TierDonut } from './PromotionMetrics/TierDonut';
import { TopSpendersTable } from './PromotionMetrics/TopSpendersTable';
import { RecentTransactionsTable } from './PromotionMetrics/RecentTransactionsTable';

const TODAY = new Date();
const MAX_YEAR = TODAY.getFullYear();

const growthHint = (g: number | null | undefined) =>
  g == null ? 'no prior period' : `${g >= 0 ? '▲' : '▼'} ${Math.abs(g * 100).toFixed(1)}% vs prev`;

const PromotionMetricsPage: React.FC = () => {
  useBreadcrumbs([
    { label: 'Promotions Management', href: '#' },
    { label: 'Performance Metrics' },
  ]);

  const [preset, setPreset] = useState<RangePreset>('year');
  const [year, setYear] = useState(MAX_YEAR);
  const [custom, setCustom] = useState({
    start: isoDay(new Date(MAX_YEAR, 0, 1)),
    end: isoDay(TODAY),
  });
  const [category, setCategory] = useState('');
  const [tier, setTier] = useState('');

  const range = useMemo(() => resolveRange(preset, { year, custom }), [preset, year, custom]);

  // `bin` is omitted on purpose: the API derives it from the span with the same
  // thresholds this toolbar assumes (≤62 days → day, ≤750 → month, else year).
  const { data, isPending, isFetching, isError, error, refetch } = usePromotionMetricsQuery({
    from: range.from,
    to: range.to,
    category: category || undefined,
    tier: tier || undefined,
  });

  // Filtered responses only contain the selected slice, so the dropdowns are
  // built from the last unfiltered window instead.
  const [options, setOptions] = useState<{ categories: string[]; tiers: string[] }>({
    categories: [],
    tiers: [],
  });
  useEffect(() => {
    if (!data || category || tier) return;
    setOptions({
      categories: data.by_category.map((s) => s.key),
      tiers: data.by_tier.map((s) => s.key),
    });
  }, [data, category, tier]);

  const withSelected = (list: string[], selected: string) =>
    selected && !list.includes(selected) ? [...list, selected] : list;

  const trend = useMemo(() => toTrendSeries(data?.trend ?? []), [data?.trend]);

  // Seeded from the revenue-sorted slices so the donut, the stacked bars and the
  // transaction dots agree on a tier's colour.
  const tierColor = useMemo(
    () => makeColorScale(data?.by_tier.map((s) => s.key) ?? []),
    [data?.by_tier],
  );
  const categoryColor = useMemo(
    () => makeColorScale(data?.by_category.map((s) => s.key) ?? []),
    [data?.by_category],
  );

  const kpis = data?.kpis;
  const errorMessage = isError ? getApiErrorMessage(error, 'Failed to fetch promotion metrics') : null;

  const stats: Stat[] = [
    { key: 'revenue', label: 'Promotion revenue', value: kpis ? money(kpis.revenue) : null, hint: growthHint(kpis?.revenue_growth), icon: DollarSign, tone: 'bg-emerald-50 text-emerald-600' },
    { key: 'orders', label: 'Promotions sold', value: kpis?.orders ?? null, hint: growthHint(kpis?.orders_growth), icon: ShoppingCart, tone: 'bg-blue-50 text-blue-600' },
    { key: 'aov', label: 'Avg. order value', value: kpis ? money(kpis.aov) : null, icon: Receipt, tone: 'bg-indigo-50 text-indigo-600' },
    { key: 'buyers', label: 'Paying buyers', value: kpis?.buyers ?? null, icon: Users, tone: 'bg-violet-50 text-violet-600' },
    { key: 'refund', label: 'Refund rate', value: kpis ? `${(kpis.refund_rate * 100).toFixed(1)}%` : null, icon: RotateCcw, tone: 'bg-amber-50 text-amber-600' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-8xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Promotion Revenue</h1>
            <p className="mt-1 text-sm text-gray-500">
              How much promotions earn — over the years, by month, by category and package tier.
            </p>
          </div>
        </div>

        <MetricsToolbar
          preset={preset}
          onPreset={setPreset}
          year={year}
          onYear={setYear}
          minYear={MIN_YEAR}
          maxYear={MAX_YEAR}
          custom={custom}
          onCustom={setCustom}
          rangeLabel={range.label}
          categories={withSelected(options.categories, category)}
          category={category}
          onCategory={setCategory}
          tiers={withSelected(options.tiers, tier)}
          tier={tier}
          onTier={setTier}
          isFetching={isFetching}
        />

        {errorMessage && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="flex-1">{errorMessage}</span>
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-md border border-red-300 bg-white px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
            >
              Retry
            </button>
          </div>
        )}

        <StatsGrid
          stats={stats}
          isLoading={isPending}
          className="lg:grid-cols-5 xl:grid-cols-5 2xl:grid-cols-5"
        />

        <RevenueTrendChart rows={trend.rows} tiers={trend.tiers} color={tierColor} isLoading={isPending} />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <CategoryBreakdown slices={data?.by_category ?? []} color={categoryColor} />
          <TierDonut slices={data?.by_tier ?? []} color={tierColor} />
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <TopSpendersTable rows={data?.top_buyers ?? []} categoryColor={categoryColor} />
          <RecentTransactionsTable
            orders={data?.recent_transactions ?? []}
            categoryColor={categoryColor}
            tierColor={tierColor}
          />
        </div>
      </div>
    </div>
  );
};

export default PromotionMetricsPage;
